import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Sparkles, ArrowRight, Gem } from "lucide-react";
import { Button } from "@/components/ui/button";
import SEO from "@/components/SEO";
import WhatsNewTimeline from "@/components/WhatsNewTimeline";
import { useReleaseNotes } from "@/hooks/useReleaseNotes";

export default function WhatsNewPublicPage() {
  const { releases, markLatestSeen } = useReleaseNotes();

  // Visiting the public page also counts as seeing the latest release,
  // so authenticated users returning here won't keep seeing the unread
  // indicator inside the app.
  useEffect(() => {
    markLatestSeen();
  }, [markLatestSeen]);

  return (
    <>
      <SEO
        title="What's New | Postmoro"
        description="See the latest Postmoro features, improvements, and bug fixes."
        path="/whats-new"
      />
      <div
        className="min-h-screen bg-[color:var(--bg-0)] text-[color:var(--text-1)]"
        data-testid="whats-new-public-page"
      >
        {/* Simple public header */}
        <header className="border-b border-[color:var(--border-subtle)]">
          <div className="max-w-[1080px] mx-auto px-4 sm:px-6 lg:px-10 h-16 flex items-center justify-between">
            <Link
              to="/"
              className="inline-flex items-center gap-2"
              data-testid="whats-new-public-brand"
            >
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-[color:var(--brand-primary)] to-[color:var(--brand-secondary)] text-[color:var(--bg-0)] shadow-[0_4px_18px_rgba(27,168,159,0.45)]">
                <Gem className="h-4 w-4" />
              </div>
              <span className="font-display text-lg font-semibold tracking-tight">
                Postmoro
              </span>
            </Link>
            <nav className="flex items-center gap-3 text-sm">
              <Link
                to="/"
                className="text-[color:var(--text-2)] hover:text-[color:var(--text-1)]"
                data-testid="whats-new-public-home-link"
              >
                Home
              </Link>
              <Link
                to="/login"
                className="text-[color:var(--text-2)] hover:text-[color:var(--text-1)]"
                data-testid="whats-new-public-signin"
              >
                Sign in
              </Link>
              <Link to="/login" data-testid="whats-new-public-signup">
                <Button
                  size="sm"
                  className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] font-semibold"
                >
                  Start free
                </Button>
              </Link>
            </nav>
          </div>
        </header>

        {/* Hero */}
        <section className="max-w-[1080px] mx-auto px-4 sm:px-6 lg:px-10 pt-14 pb-10">
          <div
            className="inline-flex items-center gap-1.5 rounded-full border border-[color:rgba(27,168,159,0.35)] bg-[color:rgba(27,168,159,0.08)] px-2.5 py-1 text-[10px] uppercase tracking-[0.18em] text-[color:var(--brand-primary)] font-semibold"
            data-testid="whats-new-public-eyebrow"
          >
            <Sparkles className="h-3 w-3" />
            Changelog
          </div>
          <h1
            className="mt-4 font-display text-3xl sm:text-5xl font-semibold tracking-tight"
            data-testid="whats-new-public-title"
          >
            What&apos;s New
          </h1>
          <p className="mt-3 text-base sm:text-lg text-[color:var(--text-2)] max-w-2xl">
            See the latest features, improvements, and fixes shipped to
            Postmoro.
          </p>
        </section>

        {/* Timeline */}
        <section className="max-w-[1080px] mx-auto px-4 sm:px-6 lg:px-10 pb-16">
          <WhatsNewTimeline releases={releases} />
        </section>

        {/* Final CTA */}
        <section
          className="max-w-[1080px] mx-auto px-4 sm:px-6 lg:px-10 pb-16"
          data-testid="whats-new-public-cta"
        >
          <div className="rounded-2xl border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] p-8 sm:p-10 text-center">
            <h2 className="font-display text-xl sm:text-2xl font-semibold tracking-tight">
              Ready to try Postmoro?
            </h2>
            <p className="mt-2 text-sm text-[color:var(--text-2)]">
              Your AI business growth assistant — free to start.
            </p>
            <Link to="/login" className="inline-block mt-5">
              <Button
                size="lg"
                className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] font-semibold"
                data-testid="whats-new-public-cta-button"
              >
                Get Started Free <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </section>

        {/* Minimal public footer */}
        <footer
          className="border-t border-[color:var(--border-subtle)] py-8 text-center text-xs text-[color:var(--text-3)]"
          data-testid="whats-new-public-footer"
        >
          <div>© {new Date().getFullYear()} Postmoro LLC</div>
          <div className="mt-2 flex items-center justify-center gap-4 flex-wrap">
            <Link to="/" className="hover:text-[color:var(--text-1)]">
              Home
            </Link>
            <Link to="/privacy" className="hover:text-[color:var(--text-1)]">
              Privacy
            </Link>
            <Link to="/terms" className="hover:text-[color:var(--text-1)]">
              Terms
            </Link>
            <a
              href="mailto:support@postmoro.com"
              className="hover:text-[color:var(--text-1)]"
            >
              Contact
            </a>
          </div>
        </footer>
      </div>
    </>
  );
}
